import React, { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, CircularProgress, IconButton,
  Tabs, Tab, List, ListItem, ListItemText, ListItemAvatar, Checkbox, ListItemButton,
} from '@mui/material'
import Avatar from 'components/Avatar'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined'
import {
  sharePortfolioFile, sharePortfolioFileWithBps,
  listPortfolioFileSharedUsers, revokePortfolioFileExternalShare, listPortfolioFileBuildingShares,
} from 'services/portfolioDocumentsService'
import { hideEmail } from 'utils/user'
import { toast } from 'react-toastify'

const ORANGE = '#D24724'

export default function SharePortfolioFileModal({ portfolioId, file, buildingPassports = [], onClose }) {
  const [tab, setTab] = useState(0)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)

  const [external, setExternal] = useState([])
  const [email, setEmail] = useState('')
  const [accessLevel, setAccessLevel] = useState('view')

  const [selectedBps, setSelectedBps] = useState({})
  const [addedBpIds, setAddedBpIds] = useState([])

  const loadShares = () => {
    setLoading(true)
    return Promise.all([
      listPortfolioFileSharedUsers(portfolioId, file.id),
      listPortfolioFileBuildingShares(portfolioId, file.id),
    ])
      .then(([sharesRes, bpRes]) => {
        setExternal(sharesRes.data.data.external || [])
        setAddedBpIds((bpRes.data.data || []).map((s) => s.buildingPassportId || s.building_passport_id))
      })
      .catch(() => toast.error('Could not load how this document is shared'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadShares() }, [])

  const submitEmail = async () => {
    const value = email.trim()
    if (!value || !value.includes('@')) { toast.error('Enter a valid email address'); return }
    setBusy(true)
    try {
      await sharePortfolioFile(portfolioId, file.id, { email: value, accessLevel })
      setEmail('')
      await loadShares()
      toast.success(`Secure link sent to ${value}`)
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to share the document')
    } finally { setBusy(false) }
  }

  const revokeExternal = async (shareId) => {
    try { await revokePortfolioFileExternalShare(portfolioId, file.id, shareId); await loadShares() }
    catch (e) { toast.error('Could not revoke the link') }
  }

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success('Link copied') }

  const toggleBp = (id) => {
    if (addedBpIds.includes(id)) return
    setSelectedBps((s) => ({ ...s, [id]: !s[id] }))
  }
  const chosenBpIds = Object.keys(selectedBps).filter((id) => selectedBps[id])

  const submitBps = async () => {
    if (chosenBpIds.length === 0) { toast.error('Select at least one Building Passport'); return }
    setBusy(true)
    try {
      const { data } = await sharePortfolioFileWithBps(portfolioId, file.id, chosenBpIds)
      toast.success(`Added to ${data.data.sharedTo} Building Passport${data.data.sharedTo === 1 ? '' : 's'}`)
      setSelectedBps({})
      await loadShares()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add to Building Passports')
    } finally { setBusy(false) }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share "{file.displayName || file.name}"</DialogTitle>
      <Tabs value={tab} onChange={(e, v) => setTab(v)}
        sx={{ px: 3, '& .Mui-selected': { color: ORANGE }, '& .MuiTabs-indicator': { backgroundColor: ORANGE } }}>
        <Tab label={`External link${external.length ? ` (${external.length})` : ''}`} />
        <Tab label="Add to Building Passports" />
      </Tabs>
      <DialogContent dividers>
        {loading && <div style={{ padding: 20, textAlign: 'center' }}><CircularProgress size={22} sx={{ color: ORANGE }} /></div>}

        {!loading && tab === 0 && (
          <>
            <p style={{ marginTop: 0, color: '#666', fontSize: 14 }}>
              Send a secure link to someone outside the organisation. No account needed.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <TextField fullWidth size="small" label="Recipient email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} />
              <TextField select size="small" label="Permission" value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)} sx={{ minWidth: 120 }}>
                <MenuItem value="view">View</MenuItem>
                <MenuItem value="edit">Edit</MenuItem>
              </TextField>
              <Button variant="contained" onClick={submitEmail} disabled={busy}
                sx={{ backgroundColor: ORANGE, '&:hover': { backgroundColor: '#bd3f20' }, whiteSpace: 'nowrap', mt: '1px' }}>
                {busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Send'}
              </Button>
            </div>
            {external.length === 0 ? (
              <p style={{ color: '#888', marginTop: 16 }}>This document has not been shared by link yet.</p>
            ) : (
              <List dense sx={{ mt: 1 }}>
                {external.map((s) => (
                  <ListItem key={s.id} secondaryAction={
                    <>
                      <IconButton edge="end" size="small" title="Copy link" onClick={() => copy(s.link)}><ContentCopyIcon fontSize="small" /></IconButton>
                      <IconButton edge="end" size="small" title="Revoke link" onClick={() => revokeExternal(s.id)}><LinkOffOutlinedIcon fontSize="small" /></IconButton>
                    </>
                  }>
                    <ListItemAvatar>
                      <Avatar email={s.email} src={s.avatar} size={36} />
                    </ListItemAvatar>
                    <ListItemText primary={hideEmail(s.email)} secondary={s.accessLevel === 'edit' ? 'View & edit' : 'View only'} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {!loading && tab === 1 && (
          <>
            <p style={{ marginTop: 0, color: '#666', fontSize: 14 }}>
              Add a copy of this document to one or more Building Passports in this portfolio. It will
              appear in each Building Passport's files (where it can then be shared with that Building
              Passport's units).
            </p>
            {buildingPassports.length === 0 ? (
              <p style={{ color: '#888' }}>You don't have access to any Building Passports in this portfolio.</p>
            ) : (
              <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
                {buildingPassports.map((bp) => {
                  const added = addedBpIds.includes(bp.id)
                  return (
                    <ListItem key={bp.id} disablePadding
                      secondaryAction={added ? <span style={{ fontSize: 11, fontWeight: 700, color: '#1f8a96' }}>ADDED</span> : null}>
                      <ListItemButton onClick={() => toggleBp(bp.id)} dense disabled={added}>
                        <Checkbox edge="start" checked={added || !!selectedBps[bp.id]} disabled={added} tabIndex={-1} disableRipple
                          sx={{ '&.Mui-checked': { color: ORANGE } }} />
                        <ListItemText primary={bp.name || bp.address || 'Building Passport'} />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tab === 1 && buildingPassports.length > 0 && (
          <Button variant="contained" onClick={submitBps} disabled={busy || chosenBpIds.length === 0}
            sx={{ backgroundColor: ORANGE, '&:hover': { backgroundColor: '#bd3f20' } }}>
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Add to ${chosenBpIds.length || ''} Building Passport${chosenBpIds.length === 1 ? '' : 's'}`.trim()}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
